var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as GroupService from "../service/group-service.js";
import { normalizeText } from "../utils.js";
import { Account } from "./Account.js";
import { ResourceProperty } from "./ResourceProperty.js";
/**
 * This class defines a Group of [[Accounts]].
 *
 * Accounts can be grouped by different meaning, like Expenses, Revenue, Assets, Liabilities and so on
 *
 * Its useful to keep organized and for high level analysis.
 *
 * @public
 */
export class Group extends ResourceProperty {
    constructor(book, payload) {
        super(payload || { createdAt: `${Date.now()}` });
        /** @internal */
        this.children = new Map();
        this.book = book;
    }
    /** @internal */
    getConfig() {
        return this.book.getConfig();
    }
    /**
     * Gets the id of this Group.
     *
     * @returns The id of this Group
     */
    getId() {
        return this.payload.id;
    }
    /**
     * Gets the name of this Group.
     *
     * @returns The name of this Group
     */
    getName() {
        return this.payload.name;
    }
    /**
     * Sets the name of the Group.
     *
     * @param name - The name to set
     *
     * @returns This Group, for chaining
     */
    setName(name) {
        this.payload.name = name;
        return this;
    }
    /**
     * Tells if the balance of this Group has been verified/audited.
     *
     * @returns True if the balance of this Group has been verified/audited
     */
    isBalanceVerified() {
        return __awaiter(this, void 0, void 0, function* () {
            const accounts = yield this.getAccounts();
            for (const account of accounts) {
                if (!account.isBalanceVerified()) {
                    return false;
                }
            }
            return true;
        });
    }
    /**
     * Tells if the Group is locked by the Book owner.
     *
     * @returns True if the Group is locked
     */
    isLocked() {
        if (this.payload.locked == null) {
            return false;
        }
        return this.payload.locked;
    }
    /**
     * Sets the locked state of the Group.
     *
     * @param locked - The locked state of the Group.
     *
     * @returns This Group, for chaining
     */
    setLocked(locked) {
        this.payload.locked = locked;
        return this;
    }
    /**
     * Gets the normalized name of this group without spaces and special characters.
     *
     * @returns The name of this group without spaces and special characters
     */
    getNormalizedName() {
        if (this.payload.normalizedName) {
            return this.payload.normalizedName;
        }
        else {
            return normalizeText(this.getName());
        }
    }
    /**
     * Gets all Accounts of this group.
     *
     * @returns All Accounts of this group
     */
    getAccounts() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.accounts) {
                return Array.from(this.accounts.values());
            }
            const id = this.getId();
            if (!id) {
                return [];
            }
            let accountsPlain = yield GroupService.getAccounts(this.book.getId(), id, this.getConfig());
            if (!accountsPlain) {
                return [];
            }
            let accounts = accountsPlain.map((acc) => new Account(this.book, acc));
            return accounts;
        });
    }
    /**
     * Gets the type of the accounts of this group.
     *
     * @returns The type for of the accounts of this group. Null if mixed
     */
    getType() {
        return this.payload.type;
    }
    /**
     * Tells if the Group is hidden on main transactions menu.
     *
     * @returns True if the Group is hidden, false otherwise
     */
    isHidden() {
        return this.payload.hidden;
    }
    /**
     * Hide/Show group on main menu.
     *
     * @param hidden - Whether to hide the group
     *
     * @returns This Group, for chaining
     */
    setHidden(hidden) {
        this.payload.hidden = hidden;
        return this;
    }
    /**
     * Tells if this is a credit (Incoming and Liabilities) group.
     *
     * @returns True if this is a credit group
     */
    isCredit() {
        return this.payload.credit;
    }
    /**
     * Tells if this is a mixed (Assets/Liabilities or Incoming/Outgoing) group.
     *
     * @returns True if this is a mixed group
     */
    isMixed() {
        return this.payload.mixed;
    }
    /**
     * Tells if the Group is permanent.
     *
     * @returns True if the Group is permanent
     */
    isPermanent() {
        return this.payload.permanent;
    }
    /**
     * Gets the parent Group.
     *
     * @returns The parent Group
     */
    getParent() {
        return this.parent;
    }
    /**
     * Sets the parent Group.
     *
     * @param group - The parent Group to set
     *
     * @returns This Group, for chaining
     */
    setParent(group) {
        if (group) {
            this.payload.parent = {
                id: group.getId(),
                name: group.getName(),
                normalizedName: group.getNormalizedName(),
            };
        }
        else {
            this.payload.parent = undefined;
        }
        return this;
    }
    /**
     * Checks if the Group has a parent.
     *
     * @returns True if the Group has a parent, otherwise false
     */
    hasParent() {
        return this.payload.parent != null;
    }
    /**
     * Gets the children of the Group.
     *
     * @returns An array of child Groups
     */
    getChildren() {
        return Array.from(this.children.values()) || [];
    }
    /** @internal */
    addChild(child) {
        const id = child.getId();
        if (id) {
            this.children.set(id, child);
        }
    }
    /**
     * Gets all descendant Groups of the current Group.
     *
     * @returns A set of descendant Groups
     */
    getDescendants() {
        const descendants = new Set();
        this.traverseDescendants(this, descendants);
        return descendants;
    }
    /**
     * Gets the IDs of all descendant Groups in a tree structure.
     *
     * @returns A set of descendant Group IDs
     */
    getDescendantTreeIds() {
        return new Set(Array.from(this.getDescendants()).map((g) => g.getId() || ""));
    }
    /**
     * Checks if the Group has any children.
     *
     * @returns True if the Group has children, otherwise false
     */
    hasChildren() {
        return this.getChildren().length > 0;
    }
    /**
     * Checks if the Group is a leaf node (i.e., has no children).
     *
     * @returns True if the Group is a leaf, otherwise false
     */
    isLeaf() {
        return this.getChildren().length === 0;
    }
    /**
     * Checks if the Group is a root node (i.e., has no parent).
     *
     * @returns True if the Group is a root, otherwise false
     */
    isRoot() {
        return this.getParent() == undefined;
    }
    /**
     * Gets the depth of the Group in the hierarchy.
     *
     * @returns The depth of the Group
     */
    getDepth() {
        if (this.depth == undefined) {
            if (this.parent) {
                this.depth = this.parent.getDepth() + 1;
            }
            else {
                this.depth = 0;
            }
        }
        return this.depth;
    }
    /**
     * Gets the root Group of the current Group.
     *
     * @returns The root Group
     */
    getRoot() {
        if (this.root == undefined) {
            if (this.parent != undefined) {
                this.root = this.parent.getRoot();
            }
            else {
                this.root = this;
            }
        }
        return this.root;
    }
    /**
     * Gets the name of the root Group.
     *
     * @returns The name of the root Group
     */
    getRootName() {
        const root = this.getRoot();
        if (root != null) {
            return root.getName() || "";
        }
        return "";
    }
    /** @internal */
    traverseDescendants(group, descendants) {
        descendants.add(group);
        const children = group.getChildren();
        if (children.length > 0) {
            for (const child of children) {
                this.traverseDescendants(child, descendants);
            }
        }
    }
    /** @internal */
    buildGroupTree(idGroupMap) {
        this.parent = undefined;
        this.depth = undefined;
        this.root = undefined;
        if (this.payload.parent) {
            const parentGroup = idGroupMap.get(this.payload.parent.id || "");
            if (parentGroup) {
                this.parent = parentGroup;
                parentGroup.addChild(this);
            }
        }
    }
    /** @internal */
    addAccount(account) {
        const id = account === null || account === void 0 ? void 0 : account.getId();
        if (id) {
            if (!this.accounts) {
                this.accounts = new Map();
            }
            this.accounts.set(id, account);
        }
    }
    /**
     * Tells if this group has any account in it.
     *
     * @returns True if this group has any account in it
     */
    hasAccounts() {
        return this.payload.hasAccounts;
    }
    /**
     * Performs create new group.
     *
     * @returns A promise that resolves to this Group
     */
    create() {
        return __awaiter(this, void 0, void 0, function* () {
            this.payload = yield GroupService.createGroup(this.book.getId(), this.payload, this.getConfig());
            this.updateGroupCache();
            return this;
        });
    }
    /**
     * Performs update group, applying pending changes.
     *
     * @returns A promise that resolves to this Group
     */
    update() {
        return __awaiter(this, void 0, void 0, function* () {
            this.payload = yield GroupService.updateGroup(this.book.getId(), this.payload, this.getConfig());
            this.updateGroupCache();
            return this;
        });
    }
    /**
     * Performs delete group.
     *
     * @returns A promise that resolves to this Group
     */
    remove() {
        return __awaiter(this, void 0, void 0, function* () {
            this.payload = yield GroupService.deleteGroup(this.book.getId(), this.payload, this.getConfig());
            this.updateGroupCache(true);
            return this;
        });
    }
    /** @internal */
    updateGroupCache(remove) {
        this.book.setGroup(this.payload, remove);
        this.book.clearCache();
    }
}
//# sourceMappingURL=Group.js.map